import { useEffect, useState } from 'react';
import { useActiveTrack } from 'react-native-track-player';

import { PlayerService } from '../components/Player/PlayerService';

type PodcastProgress = {
  position: number;
  duration: number;
};

const emptyProgress: PodcastProgress = {
  position: 0,
  duration: 0,
};

/**
 * Reads progress only for the active podcast track, so a track transition
 * cannot temporarily apply the outgoing episode's progress to another card.
 */
export const useActivePodcastProgress = (
  trackUrl: string | undefined,
  enabled: boolean,
): PodcastProgress | undefined => {
  const activeTrack = useActiveTrack();
  const [progress, setProgress] = useState<PodcastProgress>(emptyProgress);
  const isActiveTrack = enabled && !!trackUrl && activeTrack?.url === trackUrl;

  useEffect(() => {
    if (!isActiveTrack) {
      setProgress(emptyProgress);
      return;
    }

    let isMounted = true;

    const updateProgress = async () => {
      try {
        const { position, duration } = await PlayerService.getProgress();
        if (!isMounted) {
          return;
        }
        setProgress(currentProgress =>
          currentProgress.position === position && currentProgress.duration === duration
            ? currentProgress
            : { position, duration },
        );
      } catch {
        // The player may be unavailable briefly while the active track changes.
      }
    };

    updateProgress();
    const interval = setInterval(() => {
      updateProgress();
    }, 1000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [isActiveTrack]);

  return isActiveTrack ? progress : undefined;
};
